import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/auth";

/* ============================================================
 /how-it-works — public onboarding walkthrough

 Cold-traffic landing page with an auto-narrated tutorial video
 (server-generated MP4) and a numbered visual cheat sheet.
 Zero login required — anyone can watch.
 ============================================================ */

const STEPS = [
 { n: "01", title: "WATCH FIRST", body: "Landing page plays a live A.I. cypher immediately. See two rappers battle before you even sign up." },
 { n: "02", title: "CLAIM YOUR ARENA", body: "Sign up in thirty seconds. Rapper name, style, vocal type. Male or female. That's it." },
 { n: "03", title: "BUILD YOUR RAPPER", body: "Head to STUDIO. Design your look — skin, hair, chains, tats, mood. A.I. renders your photorealistic portrait." },
 { n: "04", title: "LAUNCH A CYPHER", body: "Not ready to spit? Pick two A.I. rappers and an arena, watch them battle. Great for testing the vibe." },
 { n: "05", title: "PICK YOUR ARENA", body: "Ready to step in? Choose your battleground — Octagon, Compton Alley, Underground Cypher, NYC Rooftop." },
 { n: "06", title: "SPIT YOUR VERSE", body: "Type your bars, or tap the mic and speak them. A.I. transcribes your voice in real time." },
 { n: "07", title: "JUDGE DEGAGNE", body: "Judge DeGagne scores every verse — flow, rhyme, punchlines, topic, crowd energy. He speaks the verdict out loud." },
 { n: "08", title: "CLIMB THE LADDER", body: "Wins climb the leaderboard. Top rappers get seeded into the monthly tournament. Champions get crowned." },
 { n: "09", title: "SHARE THE BATTLE", body: "Every battle gets a shareable page and a downloadable MP4. Post it anywhere. Build your name." },
 { n: "10", title: "GRAB A PASS", body: "Arena + Charts Pass $4.99/mo · Champion Pass $14.99/mo. One pass unlocks live battles AND unlimited song drops. Listening the Charts library is always free." },
];

export default function HowItWorks() {
 const [videoUrl, setVideoUrl] = useState(null);
 const [status, setStatus] = useState("checking");
 const videoRef = useRef(null);

 useEffect(() => {
 axios.get(`${API}/tutorial/status`)
 .then(r => {
 if (r.data?.state === "ready" && r.data?.url) {
 setVideoUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
 setStatus("ready");
 } else {
 setStatus(r.data?.state || "unknown");
 }
 })
 .catch(() => setStatus("unavailable"));
 }, []);

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> 60-SECOND WALKTHROUGH</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-4">
 HOW IT <span className="text-yt-red">WORKS</span>
 </h1>
 <p className="text-white/70 font-body max-w-3xl mb-10 text-lg">
 Watch the sixty-second tour, then step into the arena. No login required to watch.
 </p>

 {/* Video panel */}
 <div data-testid="how-it-works-video-panel"
 className="bg-black border-4 border-yt-yellow shadow-brutal aspect-video mb-10 relative overflow-hidden">
 {status === "ready" && videoUrl ? (
 <video
 ref={videoRef}
 data-testid="how-it-works-video"
 controls
 autoPlay
 muted
 playsInline
 className="w-full h-full object-contain bg-black"
 >
 {/* WebM first for open-source Chromium/Firefox, MP4 fallback for
 Safari/iOS/Chrome-with-proprietary-codecs */}
 <source src={videoUrl.replace(/\.mp4$/, ".webm")} type="video/webm" />
 <source src={videoUrl} type="video/mp4" />
 Your browser does not support the video tag.
 </video>
 ) : (
 <div className="w-full h-full flex flex-col items-center justify-center p-8">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-3 animate-pulse-red">
 TUTORIAL VIDEO
 </div>
 <div className="font-display text-3xl md:text-5xl text-yt-yellow mb-4">
 {status === "building" ? "STITCHING..." : "PREPARING VIDEO"}
 </div>
 <div className="font-mono text-sm text-white/70 max-w-lg text-center">
 The narrated walkthrough is being generated. In the meantime, the
 step-by-step cheat sheet below covers everything.
 </div>
 </div>
 )}
 </div>

 {/* Cheat sheet */}
 <div data-testid="how-it-works-steps" className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
 {STEPS.map(s => (
 <div key={s.n} data-testid={`step-${s.n}`}
 className="bg-yt-surface border border-yt-border p-6 hover:border-yt-yellow transition-colors">
 <div className="flex items-baseline gap-3 mb-3">
 <div className="font-mono text-yt-red text-sm tracking-widest">{s.n}</div>
 <div className="font-display text-2xl md:text-3xl text-white tracking-tight">{s.title}</div>
 </div>
 <div className="font-body text-white/80">{s.body}</div>
 </div>
 ))}
 </div>

 {/* Final CTA */}
 <div className="bg-yt-surface border-4 border-yt-red p-8 md:p-12 shadow-brutal text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">READY TO SPIT?</div>
 <div className="font-display text-4xl md:text-6xl tracking-tight mb-6">
 STEP INTO THE <span className="text-yt-red">ARENA</span>
 </div>
 <div className="flex flex-wrap gap-4 justify-center">
 <Link to="/register" data-testid="how-it-works-signup"
 className="inline-block bg-yt-yellow text-black font-display text-2xl tracking-wider px-8 py-4 shadow-brutal hover:bg-white transition-colors">
 ▶ SIGN UP FREE
 </Link>
 <Link to="/cypher" data-testid="how-it-works-cypher"
 className="inline-block bg-black text-yt-yellow border-2 border-yt-yellow font-display text-2xl tracking-wider px-8 py-4 shadow-brutal hover:bg-yt-yellow hover:text-black transition-colors">
 ▶ WATCH A CYPHER
 </Link>
 </div>
 </div>
 </div>
 );
}
